import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { hasCapability } from "@/lib/permissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Merge, Package, Pencil, Check, X as XIcon } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Machinery", "Furniture & Fixtures"];

export default function AssetNameOptions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState(CATEGORIES[0]);
  const [options, setOptions] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const startEdit = (opt) => { setEditingId(opt.id); setEditDraft(opt.name); };
  const cancelEdit = () => { setEditingId(null); setEditDraft(""); };
  const saveEdit = async (opt) => {
    const next = editDraft.trim();
    if (!next || next === opt.name) { cancelEdit(); return; }
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/asset-name-options/${opt.id}`, { name: next });
      toast.success(`Renamed to "${next}" · ${data.updated_assets} asset(s) updated`);
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Rename failed");
    } finally { setSavingEdit(false); }
  };

  useEffect(() => {
    if (user && !hasCapability(user, "manage_asset_name_options")) {
      toast.error("You don't have permission to manage asset-name dropdowns.");
      navigate("/");
    }
  }, [user, navigate]);

  const load = async () => {
    try {
      const [{ data: opts }, { data: sug }] = await Promise.all([
        api.get("/asset-name-options", { params: { category: tab } }),
        api.get("/asset-name-options/merge-suggestions", { params: { category: tab } }),
      ]);
      setOptions(opts || []);
      setSuggestions(sug?.pairs || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load options");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const addOption = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await api.post("/asset-name-options", { category: tab, name: newName.trim() });
      setNewName("");
      toast.success("Added");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to add");
    } finally { setBusy(false); }
  };

  const softDelete = async (opt) => {
    const inUse = opt.usage_count || 0;
    if (!window.confirm(inUse > 0
      ? `"${opt.name}" is used by ${inUse} asset(s). Deleting will hide it from the dropdown but existing assets keep their name. Continue?`
      : `Delete "${opt.name}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/asset-name-options/${opt.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const merge = async (pair, keepId) => {
    const keeper = keepId === pair.left.id ? pair.left : pair.right;
    const remover = keepId === pair.left.id ? pair.right : pair.left;
    if (!window.confirm(`Merge "${remover.name}" into "${keeper.name}"?
${remover.usage_count} asset(s) will be renamed to "${keeper.name}".`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/asset-name-options/merge", {
        keep_id: keeper.id,
        remove_ids: [remover.id],
        update_assets: true,
      });
      toast.success(`Merged into "${data.kept}" · ${data.renamed_assets} asset(s) renamed`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Merge failed");
    } finally { setBusy(false); }
  };

  const totalUsage = useMemo(
    () => options.reduce((s, o) => s + (o.usage_count || 0), 0),
    [options],
  );

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="asset-name-options-page">
      <div>
        <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">Master Data</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
          <Package className="w-7 h-7 text-[#7C1D3A]" /> Asset-Name Dropdowns
        </h1>
        <p className="text-slate-500 mt-1 text-sm">
          Curate the two dropdowns used when raising Machinery / Furniture & Fixtures assets or POs.
        </p>
      </div>

      {/* Category tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => setTab(cat)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${tab === cat ? "border-[#7C1D3A] text-[#7C1D3A]" : "border-transparent text-slate-500 hover:text-slate-800"}`}
            data-testid={`tab-${cat.replace(/\s|&/g, '-')}`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Suggested merges */}
      {suggestions.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/40">
          <CardHeader>
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <Merge className="w-4 h-4 text-amber-700" /> Suggested Merges
              <Badge variant="outline" className="border-amber-300 text-amber-800 bg-amber-50 ml-1">{suggestions.length}</Badge>
            </CardTitle>
            <p className="text-xs text-slate-600 mt-1">Near-duplicate names detected by fuzzy matching. Pick one canonical name; assets on the other side are renamed automatically.</p>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {suggestions.map((pair, idx) => (
              <div key={idx} className="border border-amber-200 rounded-md bg-white p-3 flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-slate-900 font-medium break-words">{pair.left.name}</div>
                      <div className="text-[10px] text-slate-500">{pair.left.usage_count} asset(s)</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => merge(pair, pair.left.id)} disabled={busy} data-testid={`keep-left-${idx}`}>
                      Keep this
                    </Button>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-slate-900 font-medium break-words">{pair.right.name}</div>
                      <div className="text-[10px] text-slate-500">{pair.right.usage_count} asset(s)</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => merge(pair, pair.right.id)} disabled={busy} data-testid={`keep-right-${idx}`}>
                      Keep this
                    </Button>
                  </div>
                </div>
                <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 font-mono">
                  {(pair.similarity * 100).toFixed(0)}% match
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Add + list */}
      <Card>
        <CardHeader className="flex flex-row items-end justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="font-heading text-base">{tab} — {options.length} option(s), {totalUsage} asset(s) using them</CardTitle>
          </div>
          <div className="flex gap-2 flex-1 md:flex-none md:w-96">
            <Input
              placeholder={`Add a new ${tab} name...`}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addOption()}
              data-testid="new-option-input"
            />
            <Button
              onClick={addOption}
              disabled={busy || !newName.trim()}
              className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
              data-testid="add-option-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-y border-slate-200 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-right px-4 py-2">Usage</th>
                <th className="text-right px-4 py-2">Added by</th>
                <th className="text-right px-4 py-2 w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {options.length === 0 && (
                <tr><td colSpan={4} className="text-center text-slate-400 py-6">No {tab} options yet</td></tr>
              )}
              {options.map((o) => (
                <tr key={o.id} className="hover:bg-slate-50" data-testid={`row-${o.id}`}>
                  <td className="px-4 py-2 text-slate-900">
                    {editingId === o.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          autoFocus
                          value={editDraft}
                          onChange={(e) => setEditDraft(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") saveEdit(o); if (e.key === "Escape") cancelEdit(); }}
                          className="h-8 max-w-xs"
                          data-testid={`rename-input-${o.id}`}
                        />
                        <Button size="sm" onClick={() => saveEdit(o)} disabled={savingEdit} className="bg-emerald-600 hover:bg-emerald-700 h-8" data-testid={`rename-save-${o.id}`}>
                          <Check className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={cancelEdit} className="h-8">
                          <XIcon className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ) : o.name}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">
                    {o.usage_count > 0 ? (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{o.usage_count} in use</Badge>
                    ) : (
                      <span className="text-slate-400 text-xs">Unused</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right text-xs text-slate-500">{o.created_by_name || "—"}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex items-center gap-0.5">
                      {editingId !== o.id && (
                        <button
                          type="button"
                          onClick={() => startEdit(o)}
                          className="text-slate-500 hover:text-[#7C1D3A] p-1 rounded hover:bg-[#fcf0f3]"
                          title="Rename (cascades to all assets using this name)"
                          data-testid={`rename-btn-${o.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => softDelete(o)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Delete (soft — existing assets keep their name)"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
